export interface Order {
  id: string;
  orderNo: string;
  date: string;
  customerName: string;
  supplierName?: string;
  branchName: string;
  deliveryMode: string;
  invoiceNo: string;
  invoiceMediaId?: string;
  invoiceAmount?: number;
  invoiceStatus?: string;
  challanNo?: string;
  challanMediaId?: string;
  challanAmount?: number;
  paymentMode?: string;
  paymentCollectedBy?: string;
  paymentCollectedAt?: string;
  supplierInvoiceNo?: string;
  supplierInvoiceMediaId?: string;
  supplierInvoiceAmount?: number;
  supplierChallanNo?: string;
  supplierChallanMediaId?: string;
  supplierChallanAmount?: number;
  eta?: string;
  items?: Array<{
    partId?: string;
    rootPartNumber?: string;
    orderedQuantity?: number;
    givenQuantity?: number;
  }>;
  status: string;
}

export interface ShipmentDetails {
  id: string;
  shipmentNo: string;
  orderNo: string;
  date: string;
  customerName: string;
  customerType: string;
  destinationAddress: string;
  pickupAddress: string;
  courier: string;
  trackingNo?: string;
  trackingUrl?: string;
  status: ShipmentStatus | string;
  statusHistory: Array<{
    status: string;
    timestamp: string;
    remarks: string;
    hasData?: boolean;
  }>;
  estimatedDelivery: string;
  actualDelivery?: string;
  deliveryInstructions: string;
  lastUpdatedAt?: string;
  packageDetails: {
    orderCount: number;
    totalItemsQuantity: number;
  };
  billingDetails: {
    shippingCharges: number;
  };
  contactDetails: {
    senderName: string;
    senderPhone: string;
    receiverName: string;
    receiverPhone: string;
  };
  orders: Order[];
}

export enum ShipmentStatus {
  CREATED = "CREATED",
  REQUESTED = "REQUESTED",
  ASSIGNED = "ASSIGNED",
  PICKED_UP = "PICKED_UP",
  IN_TRANSIT = "IN_TRANSIT",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  CANCELLED = "CANCELLED",
}

export enum EngagementMode {
  PLATFORM = "PLATFORM",
  SUBSCRIBED = "SUBSCRIBED",
  EXPIRED = "EXPIRED",
}

export enum PorterDeliveryTrackingStatus {
  OPEN = "open",
  ACCEPTED = "accepted",
  LIVE = "live",
  ENDED = "ended",
  CANCELLED = "cancelled",
}

export enum VehicleType {
  TWO_WHEELER = "TWO_WHEELER",
  OTHER = "OTHER",
}

export interface Order {
  id: string;
  orderNo: string;
  date: string;
  customerName: string;
  branchName: string;
  deliveryMode: string;
  invoiceInfo: string;
  orderValue: string;
  weight: string;
  dimensions: string;
  status: string;
}

export interface Customer {
  _id: string;
  name: string;
  type: string;
  totalShipmentsCount?: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  totalUndeliveredShipments?: number;
  shipmentAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface Supplier {
  _id: string;
  name: string;
  totalShipmentsCount: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  totalUndeliveredShipments?: number;
  supplierAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface Shipment {
  shipmentId: string;
  customerId: string;
  supplierId: string;
  orderNumbers: string[];
  invoiceNumbers?: string[];
  invoiceStatus?: string[];
  supplierInvoiceNumbers?: string[];
  challanNumbers?: string[];
  supplierChallanNumbers?: string[];
  totalItems: number;
  totalQuantity: number;
  totalValue: number;
  status: ShipmentStatus;
  pickupDetails: {
    address: {
      streetAddress1: string;
      streetAddress2?: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
      lat: number;
      lng: number;
      contactDetails: {
        name: string;
        phoneNumber: string;
      };
    };
  };
  dropDetails: {
    address: {
      streetAddress1: string;
      streetAddress2?: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
      lat: number;
      lng: number;
      contactDetails: {
        name: string;
        phoneNumber: string;
      };
    };
  };
  deliveryBooking?: {
    requestId?: string;
    providerOrderId?: string;
    trackingUrl?: string;
    estimatedPickupTime?: Date;
    estimatedDeliveryTime?: number;
    estimatedFare?: {
      currency?: string;
      minorAmount?: number;
    };
    actualFare?: {
      currency?: string;
      minorAmount?: number;
    };
    deliveryInstructions?: string;
    bookedAt?: Date;
  };
  deliveryTracking?: {
    trackingStatus?: PorterDeliveryTrackingStatus;
    currentLocation?: {
      lat?: number;
      lng?: number;
      updatedAt?: Date;
    };
    deliveryAgent?: {
      name?: string;
      phoneNumber?: string;
      alternatePhoneNumber?: string;
      vehicleDetails?: {
        vehicleNumber?: string;
        vehicleType?: string;
      };
    };
    timeline?: {
      assignedToAgentAt?: Date;
      pickedUpAt?: Date;
      deliveredAt?: Date;
      cancelledAt?: Date;
      reopenedAt?: Date;
      bookedAt?: Date;
    };
    lastUpdatedAt?: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface FirstLevelEntity {
  _id: string;
  name: string;
  type: string;
  totalShipmentsCount: number;
  totalUndeliveredShipments: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  engagementMode?: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface SecondLevelEntity {
  _id: string;
  name: string;
  totalShipmentsCount: number;
  totalUndeliveredShipments: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  engagementMode?: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface ShipmentModeFacet {
  count: number;
  mode: string | null;
}

export interface CptSlabLevelFacet {
  level: number;
  count: number;
}

export interface ShipmentFacets {
  cptSlabLevels: CptSlabLevelFacet[];
  dispatchMode: ShipmentModeFacet[];
}

export interface ShipmentFacetsResponse {
  shipments?: Shipment[];
  facets: ShipmentFacets;
}

export enum ShipmentFilterKeys {
  DISPATCH_MODE = "dispatchMode",
  CPT_SLAB_LEVELS = "cptSlabLevels",
  INCLUDE_STATUS = "includeStatus",
  EXCLUDE_STATUS = "excludeStatus",
  GROUP_BY = "groupBy",
  CUSTOMER_ID = "customerId",
  SUPPLIER_ID = "supplierId",
  FACETS = "facets",
  LIMIT = "limit",
}

export interface ShipmentFilters {
  dispatchMode?: string | null;
  cptSlabLevels?: number[];
}
